import styled, { keyframes, css, useTheme } from 'styled-components'
import { useRef, useState, useEffect, ReactElement } from 'react'
import { useGetSize } from 'Hooks/useGetSize'
import * as d3 from 'd3'
import { AnimatedNote } from '../AnimatedNote'
import { useMediaQuery } from 'styled-breakpoints/use-media-query'
import { Note, Label, Tick, ChartTitle } from 'theme/styles'
import { useSpring, animated, useTransition } from '@react-spring/web'
import { CanvasScatterplot } from './CanvasScatterplot'

const Chart = ({ data, step }: { data: any; step: number }) => {
  const { breakpoints } = useTheme()
  const isMobile = useMediaQuery(breakpoints.down('md'))
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const { width } = useGetSize(containerRef)
  const height = isMobile ? width * 1.65 : width * 0.75

  const xDomain = [1952, 2030]
  const yDomain = [50, 170]
  const margin = {
    top: 30,
    right: 10,
    bottom: 30,
    left: 80,
  }
  const tickXLength = 10

  const xScale = d3
    .scaleLinear()
    .domain(xDomain as any)
    .range([margin.left, width - margin.right])

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([height - margin.bottom, margin.top])

  const xTicks = isMobile ? [1960, 1980, 2000, 2020] : [1960, 1980, 2000, 2020]
  const yTicks = [50, 75, 100, 125, 150]
  const yTicksLabels: { [key: number]: string } = {
    50: '19 fév.',
    75: '16 mars',
    100: '10 avr.',
    125: '5 mai',
    150: '30 mai',
  }

  return (
    <Container ref={containerRef}>
      <CanvasScatterplot
        data={data}
        stage={step}
        xScale={xScale}
        yScale={yScale}
        width={width}
        height={height}
      />

      <Svg ref={svgRef} width={width} height={height}>
        {/* Y grid */}
        {/* {xTicks.map((value) => (
          <GridLine
            x1={xScale(value)}
            x2={xScale(value)}
            y1={margin.top}
            y2={height - margin.bottom}
          />
        ))} */}

        {/* X ticks */}
        {xTicks.map((value) => (
          <Tick
            x1={xScale(value)}
            x2={xScale(value)}
            y1={height - margin.bottom + tickXLength}
            y2={height - margin.bottom}
          />
        ))}

        {/* X grid */}
        {yTicks.map((value) => {
          return (
            <GridLine
              x1={0}
              x2={width - margin.right}
              y1={yScale(value)}
              y2={yScale(value)}
            />
          )
        })}

        <BottomLine
          x1={0}
          x2={width - margin.right}
          y1={height - margin.bottom}
          y2={height - margin.bottom}
        />

        <MarkerLine
          style={{
            opacity: step < 3 ? 1 : 0,
          }}
          x1={0}
          x2={width - margin.right}
          y1={yScale(79)}
          y2={yScale(79)}
        />

        <Marker
          style={{
            opacity: step === 4 ? 1 : 0,
            // strokeDasharray: `${width} ${width}`,
            // strokeDashoffset: step === 4 ? 0 : width,
          }}
          x1={xScale(1951)}
          x2={xScale(2025)}
          y1={yScale(118)}
          y2={yScale(110)}
        />

        <BabyBoomerArea
          style={{
            opacity: step === 2 ? 0.25 : 0,
          }}
          x={margin.left - 20}
          y={yScale(115)}
          width={width - margin.right - margin.left + 20}
          height={yScale(75) - yScale(115)}
        />
      </Svg>

      {/* Y labels */}
      {yTicks.map((value, i) => (
        <Label
          style={{
            top: yScale(value) + (i === 0 ? -22 : 5),
            left: 0,
            transform: 'translateX(0)',
            textAlign: 'right',
            width: 50,
          }}
        >
          {yTicksLabels[value]}
        </Label>
      ))}

      {/* X labels */}
      {xTicks.map((value) => (
        <Label style={{ left: xScale(value), top: height - 6 }}>{value}</Label>
      ))}

      {/* Line on 2024 for reference */}
      <NoteWomen
        style={{
          top: yScale(78) - 24,
          left: width - margin.right - (isMobile ? 80 : 150),
          transform: 'translateY(-50%)',
          position: 'absolute',
          textAlign: 'right',
          opacity: step < 3 ? 1 : 0,
        }}
      >
        Début du printemps
      </NoteWomen>

      <AnimatedNote
        show={step < 2}
        chartDimensions={[width, height]}
        textPosition={[xScale(2022), yScale(125)]}
        arrowHeadposition={[xScale(2024) + 4, yScale(92) - 6]}
      >
        Cette année, le déploiement des feuilles de notre marronnier de Versoix
        a eu lieu le{' '}
        <strong>
          1<sup>er</sup> avril 2024
        </strong>
      </AnimatedNote>

      <AnimatedNote
        show={step === 2}
        chartDimensions={[width, height]}
        textPosition={[isMobile ? xScale(2000) : xScale(2009), yScale(130)]}
        arrowHeadposition={[xScale(2006) - 1, yScale(115) - 7]}
      >
        Apparition des feuilles la plus tardive: <strong>25 avril 2006</strong>
      </AnimatedNote>

      <AnimatedNote
        show={step === 2}
        chartDimensions={[width, height]}
        textPosition={[isMobile ? xScale(2005) : xScale(2012), isMobile ? yScale(65) : yScale(70)]}
        arrowHeadposition={[xScale(2009) - 9, yScale(74) + 4]}
      >
        Apparition des feuilles la plus précoce: <strong>15 mars 2008</strong>
      </AnimatedNote>

      <AnimatedNote
        show={step === 2}
        chartDimensions={[width, height]}
        textPosition={[xScale(1979), isMobile ? yScale(72) : yScale(70)]}
        arrowHeadposition={[xScale(1986), yScale(75)]}
      >
        Écart maximal de plus d'<strong>un mois</strong>
      </AnimatedNote>

      <AnimatedNote
        show={step === 3}
        chartDimensions={[width, height]}
        textPosition={[isMobile ? xScale(1974) : xScale(1994), yScale(170)]}
        arrowHeadposition={[xScale(1984) + 7, yScale(167) - 2]}
      >
        Apparition des feuilles la plus tardive{' '}
        <strong>La Brévine&nbsp;(NE)</strong> en 1984 Altitude: 1043m
      </AnimatedNote>

      <AnimatedNote
        show={step === 3}
        chartDimensions={[width, height]}
        textPosition={[xScale(1970), isMobile ? yScale(78) : yScale(68)]}
        arrowHeadposition={[xScale(1961) + 7, yScale(59) - 7]}
      >
        Apparition des feuilles la plus précoce{' '}
        <strong>Aurigeno&nbsp;(TI)</strong> en 1961 Altitude: 341m
      </AnimatedNote>

      <ChartTitle
        style={{
          top: 0,
          left: 0,
          transform: 'translateX(0)',
          textAlign: 'left',
          letterSpacing: 0.85,
          width: 250,
        }}
      >
        <strong>Date du déploiement des feuilles de marronnier (50%)</strong>
      </ChartTitle>

      {/* <MainNote
        style={{
          top: yScale(50000),
          left: xScale(30),
        }}
      >
        Répartition de la population <strong>en {year}</strong>
      </MainNote> */}
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const GridLine = styled.line`
  // stroke: ${({ theme }) => `rgba(${theme.textColorOnBackground}, 0.1)`};
  stroke: var(--text-color);
  opacity: 0.1;
  stroke-width: 1px;
`

const Marker = styled.line`
  stroke: ${({ theme }) => `rgba(${theme.yellowColor}, 1)`};
  stroke-dasharray: 12px 2px;
  stroke-width: 4px;
  transition: all 1s ease;
`

const BabyBoomerArea = styled.rect`
  fill: ${({ theme }) => `rgba(${theme.yellowColor}, 0.65)`};
  opacity: 0.25;
  stroke: none;
  mix-blend-mode: multiply;
  transition: opacity 0.3s ease;
`

const BottomLine = styled.line`
  // stroke: ${({ theme }) => `rgb(${theme.textColorOnBackground})`};
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const MarkerLine = styled.line`
  stroke: var(--text-color);
  stroke-dasharray: 9px 2px;
  stroke-width: 2px;
`

const NoteWomen = styled(Note)`
  font-weight: bold;
  //color: ${({ theme }) => `rgb(${theme.textColorOnBackground})`};
  color: var(--text-color);
  -webkit-font-smoothing: antialiased;
`

const Svg = styled.svg`
  display: block;
  position: relative;
  z-index: 1;
`

export default Chart
